import { useEffect, useState } from "react";
import { ArrowRight, Menu, ShieldCheck, X } from "lucide-react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  contactDetails,
  footerStatement,
  guardianLoginUrl,
  logoPath,
  navItems,
} from "@/lib/siteContent";

const navLinkClasses = ({ isActive }) =>
  [
    "rounded-full px-4 py-2 text-sm font-medium transition-all duration-200",
    isActive
      ? "bg-brand-navy text-white shadow-sm"
      : "text-slate-700 hover:bg-brand-mist hover:text-brand-navy",
  ].join(" ");

export const SiteLayout = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setIsMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className="page-shell min-h-screen">
      <header className="sticky top-0 z-50 border-b border-white/60 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="flex items-center gap-3"
            data-testid="site-header-logo-link"
            aria-label="CareFleet Transport home"
          >
            <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <img
                src={logoPath}
                alt="CareFleet Transport Ltd logo"
                className="h-full w-full object-contain p-1"
                data-testid="site-header-logo-image"
              />
            </div>
            <div className="hidden sm:block">
              <p className="font-heading text-lg font-bold text-brand-navy" data-testid="site-header-brand-name">
                CareFleet Transport Ltd
              </p>
              <p className="text-sm text-slate-500" data-testid="site-header-brand-tagline">
                Specialist transport with digital control.
              </p>
            </div>
          </Link>

          <nav className="hidden items-center gap-1 lg:flex">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={navLinkClasses}
                data-testid={`desktop-nav-${item.label.toLowerCase()}-link`}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            <Button variant="outline" asChild className="rounded-full border-brand-navy/20 px-5 text-brand-navy">
              <a
                href={guardianLoginUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="header-login-button"
              >
                Login
              </a>
            </Button>
            <Button asChild className="rounded-full bg-brand-navy px-5 text-white hover:bg-brand-navy/90">
              <Link to="/contact" data-testid="header-request-demo-button">
                Request Demo
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>

          <button
            type="button"
            className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm lg:hidden"
            onClick={() => setIsMenuOpen((open) => !open)}
            data-testid="mobile-menu-toggle-button"
            aria-label="Toggle navigation menu"
          >
            {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {isMenuOpen ? (
          <div className="border-t border-slate-200 bg-white/95 px-4 py-4 shadow-lg lg:hidden">
            <div className="mx-auto flex max-w-7xl flex-col gap-2">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={navLinkClasses}
                  data-testid={`mobile-nav-${item.label.toLowerCase()}-link`}
                >
                  {item.label}
                </NavLink>
              ))}
              <div className="mt-3 flex flex-col gap-3 border-t border-slate-100 pt-3">
                <Button variant="outline" asChild className="rounded-full border-brand-navy/20 text-brand-navy">
                  <a
                    href={guardianLoginUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="mobile-login-button"
                  >
                    Login
                  </a>
                </Button>
                <Button asChild className="rounded-full bg-brand-navy text-white hover:bg-brand-navy/90">
                  <Link to="/contact" data-testid="mobile-request-demo-button">
                    Request Demo
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        ) : null}
      </header>

      <main>
        <Outlet />
      </main>

      <footer className="border-t border-slate-200/80 bg-slate-950 text-white">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 lg:grid-cols-[1.1fr_0.8fr_0.8fr] lg:px-8">
          <div className="space-y-5">
            <div className="flex items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-2xl border border-white/10 bg-white">
                <img
                  src={logoPath}
                  alt="CareFleet Transport Ltd logo"
                  className="h-full w-full object-contain p-1"
                  data-testid="footer-logo-image"
                />
              </div>
              <div>
                <p className="font-heading text-xl font-bold" data-testid="footer-brand-name">
                  CareFleet Transport Ltd
                </p>
                <p className="text-sm text-slate-300" data-testid="footer-positioning-statement">
                  {footerStatement}
                </p>
              </div>
            </div>
            <div className="soft-card bg-white/5 p-6 text-slate-100 backdrop-blur">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-brand-teal">
                <ShieldCheck className="h-4 w-4" />
                Core positioning
              </div>
              <p className="font-heading text-2xl font-semibold leading-tight" data-testid="footer-core-message">
                Transport is just the vehicle. CareFleet is the system behind it.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Button asChild className="rounded-full bg-white px-5 text-brand-navy hover:bg-slate-100">
                <Link to="/contact" data-testid="footer-request-demo-button">
                  Request Demo
                </Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full border-white/20 bg-transparent px-5 text-white hover:bg-white/10">
                <Link to="/contact" data-testid="footer-speak-to-team-button">
                  Speak to Our Team
                </Link>
              </Button>
            </div>
          </div>

          <div>
            <h3 className="font-heading text-lg font-semibold" data-testid="footer-links-heading">
              Explore
            </h3>
            <ul className="mt-5 space-y-3">
              {navItems.map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className="text-slate-300 hover:text-white"
                    data-testid={`footer-${item.label.toLowerCase()}-link`}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-heading text-lg font-semibold" data-testid="footer-contact-heading">
              Contact
            </h3>
            <ul className="mt-5 space-y-4 text-slate-300">
              {contactDetails.map((item) => (
                <li key={item.label} data-testid={`footer-contact-${item.label.toLowerCase()}`}>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                    {item.label}
                  </p>
                  {item.href ? (
                    <a href={item.href} className="mt-1 inline-block text-base hover:text-white">
                      {item.value}
                    </a>
                  ) : (
                    <p className="mt-1 text-base">{item.value}</p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10">
          <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-5 text-sm text-slate-400 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
            <p data-testid="footer-copyright">© {new Date().getFullYear()} CareFleet Transport Ltd. All rights reserved.</p>
            <p data-testid="footer-login-note">Login opens the separate CareFleet Guardian platform.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};